import React from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';

import {
  SuggestionOptionButton,
  type SuggestionOptionClickPayload,
} from './SuggestionOptionButton';
import styles from './styles.module.css';

import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';

export type SuggestionOption<TValue = string> = {
  key: string;
  topLabel?: string;
  displayLabel: string;
  value: TValue;
};

export type SuggestionOptionSelection<TValue = string> = {
  event: React.MouseEvent<HTMLButtonElement>;
  value: TValue;
  index: number;
};

type Props<TValue> = {
  options: SuggestionOption<TValue>[];
  selectedIndex?: number;
  onSelect: (selection: SuggestionOptionSelection<TValue>) => void;
  recommendedIndex?: number;
  recommendationLabel?: string;
  isLoading?: boolean;
};

export function SuggestionOptionButtons<TValue = string>({
  options,
  selectedIndex,
  onSelect,
  recommendedIndex,
  recommendationLabel,
  isLoading,
}: Props<TValue>) {
  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        {options.map((option) => (
          <Placeholder
            key={option.key}
            shape={PlaceHolderShape.RECTANGLE}
            width="100%"
            height="56px"
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {options.map((option, index) => {
        const isRecommended = index === recommendedIndex;
        const isSelected = selectedIndex === index;
        const ariaPressed =
          selectedIndex !== undefined ? isSelected : undefined;

        return (
          <div className={styles.buttonWrapper} key={option.key}>
            <SuggestionOptionButton
              isSelected={isSelected}
              isRecommended={isRecommended}
              ariaPressed={ariaPressed}
              topLabel={option.topLabel}
              displayLabel={option.displayLabel}
              value={option.value}
              onClick={({
                event,
                value,
              }: SuggestionOptionClickPayload<TValue>) =>
                onSelect({ event, value, index })
              }
            />
            {isRecommended && recommendationLabel && (
              <Caption
                variant="lg-regular"
                className={styles.recommendationText}
              >
                ({recommendationLabel})
              </Caption>
            )}
          </div>
        );
      })}
    </div>
  );
}
